import { gql } from "@/__generated__/gql";

/** A file attached to a message, for showing what was sent while it is read. */
export const MESSAGE_FILE_QUERY = gql(`
  query MessageFile($id: ID!) {
    node(id: $id) {
      ... on MessageFileObject {
        id
        identifier
        name
        file {
          name
          url
        }
      }
    }
  }
`);

export const NODE_QUERY = gql(`
  query Node($id: ID!, $startDate: DateTime, $endDate: DateTime) {
    node(id: $id) {
      ... on IObject {
        ...Object
      }
      ... on RuleDefinitionObject {
        ...Rule
        ruleStatus: status
      }
    }
  }
`);

/**
 * Whether one record carries a search embedding.
 *
 * Its own query, asked by the badge that shows it: a boolean that costs an
 * index lookup per record has no business on a fragment a list reads.
 */
export const NODE_EMBEDDING_STATE = gql(`
  query NodeEmbeddingState($id: ID!) {
    node(id: $id) {
      id
      ... on IObject {
        isEmbedded
      }
    }
  }
`);

export const NODE_QUERY_WITH_PENDING_CHANGE = gql(`
  query NodeWithPendingChange($id: ID!, $startDate: DateTime, $endDate: DateTime, $pendingSession: String) {
    node(id: $id, pendingSession: $pendingSession) {
      ... on IObject {
        ...Object
        pendingChange {
          ...PendingChange
        }
      }
      ... on RuleDefinitionObject {
        ...Rule
        ruleStatus: status
      }
    }
  }
`);

export const PENDING_CHANGE_QUERY = gql(`
  query PendingChangeQuery($identifier: String!) {
    pendingChange(identifier: $identifier) {
      ...PendingChange
    }
  }
`);

export const PUBLIC_NODE_QUERY = gql(`
  query PublicNode($id: ID!) {
    node(id: $id) {
      ... on PublicTransactionObject {
        ...PublicTransaction
      }
      ...on PublicDocumentObject {
        ...PublicDocument
      }
      ... on PublicContactObject {
        ...PublicContact
      }
    }
  }
`);

/**
 * Books shared with someone, as the link sent to them reads them before they
 * sign in: whose books, who shares them, and with whom.
 */
export const PUBLIC_EXTERNAL_GRANT_QUERY = gql(`
  query PublicExternalGrant($id: ID!) {
    node(id: $id) {
      ... on PublicExternalGrantObject {
        id
        identifier
        entityName
        isUserEntity
        sharedByName
        sharedByEmailAddress
        emailAddress
        granteeName
      }
    }
  }
`);

/**
 * An invitation to join a business, as the link sent with it reads it before
 * its reader signs in: the business, and the address it was sent to.
 */
export const PUBLIC_ACCOUNT_QUERY = gql(`
  query PublicAccount($id: ID!) {
    node(id: $id) {
      ... on PublicAccountObject {
        id
        identifier
        entityName
        emailAddress
      }
    }
  }
`);

// Data Queries
export const ACCOUNTS_QUERY = gql(`
  query Accounts {
    accounts {
      data {
        id
        identifier
        isOwner
        isUserAccount
        isEntityAccount
        isExternal
        entity {
          ...Entity
        }
      }
    }
  }
`);

/**
 * The accounting firms Neoledger verified, by name: those a client finds to
 * share their books with, beside asking anyone by their address.
 */
export const VERIFIED_FIRMS_QUERY = gql(`
  query VerifiedFirms {
    verifiedFirms {
      data {
        id
        identifier
        name
      }
    }
  }
`);

/**
 * The books of the person's businesses shared with firms, and the books others
 * share with them - each asked for or standing.
 */
export const EXTERNAL_GRANTS_QUERY = gql(`
  query ExternalGrants {
    externalGrants {
      data {
        ...ExternalGrant
        delegations {
          ...ExternalGrantDelegation
        }
      }
    }
  }
`);

/**
 * The books shared with a firm, for its owners to hand on: each business's,
 * and who at the firm works in them, as what.
 */
export const EXTERNAL_GRANTS_RECEIVED_QUERY = gql(`
  query ExternalGrantsReceived($entityIdentifier: String!) {
    externalGrantsReceived(entityIdentifier: $entityIdentifier) {
      data {
        grant {
          ...ExternalGrant
        }
        delegations {
          ...ExternalGrantDelegation
        }
      }
    }
  }
`);

/** A business's roles: what each allows its holders besides reading its books. */
export const ENTITY_ROLES_QUERY = gql(`
  query EntityRoles($id: ID!) {
    node(id: $id) {
      ... on EntityObject {
        id
        identifier
        roles {
          ...Role
        }
      }
    }
  }
`);

/**
 * The accountant's portal: each business whose books are handed on to them,
 * and how its books stand - a client's are those the same person shared.
 */
export const EXTERNAL_CLIENTS_QUERY = gql(`
  query ExternalClients {
    externalClients {
      data {
        grant {
          ...ExternalGrant
        }
        roleName
        permissions
        business {
          entityIdentifier
          name
          isUserEntity
          waitingCount
          missingDocumentCount
          latestPaymentOn
          closedThrough
        }
      }
    }
  }
`);

/**
 * The grouped totals a chart is drawn from.
 *
 * A thread's ``chart`` call keeps the question - what to group, over what,
 * filtered how - and this asks it again each time the chart is drawn, so the
 * chart is of the ledger as it stands rather than of the numbers as they were
 * when the agent ran. Same service method the agent's own summary uses, so the
 * chart and the sentence under it cannot disagree.
 */
export const SUMMARY_QUERY = gql(`
  query Summary(
    $sourceName: String!
    $title: String
    $groupBy: [SummaryGroupByInput!]!
    $aggregate: [SummaryAggregateInput!]
    $filters: JSON
    $search: String
    $orderBy: [String!]
    $entityIdentifiers: [String!]
    $pendingSession: String
  ) {
    summary(
      sourceName: $sourceName
      title: $title
      groupBy: $groupBy
      aggregate: $aggregate
      filters: $filters
      search: $search
      orderBy: $orderBy
      entityIdentifiers: $entityIdentifiers
      pendingSession: $pendingSession
    ) {
      data {
        ...ChartData
      }
      errors {
        ...Errors
      }
    }
  }
`);

export const BILLING_PLANS_QUERY = gql(`
  query BillingPlans {
    billingPlans {
      data {
        ...BillingPlan
      }
    }
  }
`);

export const CASHFLOW_STATEMENT_QUERY = gql(`
  query CashFlowStatement(
    $entityIdentifier: String
    $startDate: DateTime
    $endDate: DateTime
    $frequency: FrequencyEnum
    $mode: MetricModeEnum
  ) {
    statements {
      cashFlowStatement(
        entityIdentifier: $entityIdentifier
        startDate: $startDate
        endDate: $endDate
        frequency: $frequency
        mode: $mode
      ) {
        data {
          ...CashFlowStatement
        }
        errors {
          ...Errors
        }
      }
    }
  }
`);

export const USER_QUERY = gql(`
  query User {
    user {
      data {
        id
        ...User
      }
    }
  }
`);

export const PENDING_INVITATIONS_QUERY = gql(`
  query PendingInvitations {
    onboardingStatus {
      data {
        pendingInvitations {
          id
          identifier
          entity {
            ...Entity
          }
        }
      }
    }
  }
`);

export const FINANCIAL_ACCOUNTS_CONNECTION_QUERY = gql(`
  query FinancialAccounts(
    $entityIdentifiers: [String!]
    $filter: FinancialAccountFilter,
    $first: Int,
    $after: String
  ) {
    financialAccountsConnection(entityIdentifiers: $entityIdentifiers) {
      data(
        first: $first,
        after: $after,
        filter: $filter
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...FinancialAccount
          }
        }
      }
    }
  }
`);

export const DOCUMENTS_CONNECTION_QUERY = gql(`
  query Documents(
    $entityIdentifiers: [String!]
    $filter: DocumentFilter
    $order: [DocumentOrder!]
    $first: Int
    $after: String
  ) {
    documentsConnection(entityIdentifiers: $entityIdentifiers) {
      data(
        first: $first
        after: $after
        filter: $filter
        order: $order
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            ...Document
          }
        }
      }
    }
  }
`);

/** Group-tab payload: line items are not on the base Document fragment. */
/** A bank account's statements, for its page's Statements group. */
export const FINANCIAL_ACCOUNT_STATEMENTS_QUERY = gql(`
  query FinancialAccountStatements($id: ID!) {
    node(id: $id) {
      ... on FinancialAccountObject {
        id
        statements {
          id
          identifier
          name
          description
          dateIssued
          createdAt
          fileProcessingState
          statement {
            periodStart
            periodEnd
            openingBalance
            closingBalance
            total
          }
        }
      }
    }
  }
`);

export const ENTITY_COMPANY_DOCUMENTS_QUERY = gql(`
  query EntityCompanyDocuments($id: ID!) {
    node(id: $id) {
      ... on EntityObject {
        id
        companyDocuments {
          id
          identifier
          name
          description
          type
          dateIssued
          createdAt
        }
      }
    }
  }
`);

// A business's invoice templates, its default first: for its settings page.
export const DOCUMENT_TEMPLATES_QUERY = gql(`
  query DocumentTemplates($entityIdentifiers: [String!]) {
    documentTemplates(entityIdentifiers: $entityIdentifiers) {
      data {
        ...DocumentTemplate
      }
    }
  }
`);

// What an invoice's preview reads of it, for the preview alone: the look it
// prints with - the document's own record names only which template - and
// whether the page it is shared at takes a card for it, which asks after its
// business's Stripe.
export const DOCUMENT_PREVIEW_QUERY = gql(`
  query DocumentPreview($id: ID!, $pendingSession: String) {
    node(id: $id, pendingSession: $pendingSession) {
      ... on DocumentObject {
        id
        identifier
        canAcceptPayments
        template {
          ...DocumentTemplate
        }
      }
    }
  }
`);

// Whether anything is booked against a document - which keeps it from being
// read again - asked of the one document being read again, not of every row.
export const DOCUMENT_IS_BOOKED_QUERY = gql(`
  query DocumentIsBooked($id: ID!) {
    node(id: $id) {
      ... on DocumentObject {
        id
        identifier
        isBooked
      }
    }
  }
`);

export const DOCUMENT_LINE_ITEMS_QUERY = gql(`
  query DocumentLineItems($id: ID!, $pendingSession: String) {
    node(id: $id, pendingSession: $pendingSession) {
      ... on DocumentObject {
        id
        identifier
        name
        reference
        description
        amountTotal
        amountDue
        lineItems {
          id
          identifier
          metadata
          effectiveDate
          ...PrintedLineItem
          tax {
            id
            identifier
            rates {
              rate
            }
          }
          chartedAccount {
            id
            identifier
            name
          }
        }
      }
    }
  }
`);

export const DOCUMENT_PAYMENT_SCHEDULE_QUERY = gql(`
  query DocumentPaymentSchedule($id: ID!, $pendingSession: String) {
    node(id: $id, pendingSession: $pendingSession) {
      ... on DocumentObject {
        id
        identifier
        amountTotal
        paymentSchedule {
          id
          identifier
          name
          percent
          amount
          dateDue
          status
          datePaid
          dateClaimed
          claimedReference
          dateAcknowledged
        }
      }
    }
  }
`);

/**
 * Who else is offered the documents a suggestion would match: asked for the one
 * suggestion being looked at, not for every suggestion on every payment.
 */
export const RULE_SUGGESTION_COMPETING_QUERY = gql(`
  query RuleSuggestionCompeting($id: ID!) {
    node(id: $id) {
      ... on RuleSuggestionObject {
        id
        identifier
        competingSuggestions {
          id
          identifier
          transaction {
            id
            identifier
            description
            amount
            effectiveDate
          }
          ruleSuggestionDocumentOptions {
            id
            identifier
            document {
              id
              identifier
            }
          }
        }
      }
    }
  }
`);

// The other payments that may pay the rest a document match's payment leaves of
// its document - a receipt paid on two cards, an invoice in two e-transfers -
// and whether each fits: for its card to offer, to be booked with it.
export const RULE_SUGGESTION_OTHER_PARTS_QUERY = gql(`
  query RuleSuggestionOtherParts($id: ID!) {
    node(id: $id) {
      ... on RuleSuggestionObject {
        id
        identifier
        ruleSuggestionDocumentOptions {
          id
          identifier
          otherParts {
            fits
            transaction {
              id
              identifier
              description
              amount
              effectiveDate
              journalEntry {
                id
                identifier
              }
            }
          }
        }
      }
    }
  }
`);

// The bank payments that pay a document, for its Payments tab: each with what
// of the document it pays.
export const DOCUMENT_PAYMENTS_QUERY = gql(`
  query DocumentPayments($id: ID!) {
    node(id: $id) {
      ... on DocumentObject {
        id
        identifier
        paymentShares {
          amountDocument
          transaction {
            id
            identifier
            description
            effectiveDate
            financialAccount {
              id
              identifier
              name
            }
          }
        }
      }
    }
  }
`);

/** Public invoice page payload: line items are not on the base PublicDocument fragment. */
export const PUBLIC_DOCUMENT_LINE_ITEMS_QUERY = gql(`
  query PublicDocumentLineItems($id: ID!) {
    node(id: $id) {
      ... on PublicDocumentObject {
        id
        identifier
        lineItems {
          id
          identifier
          metadata
          ...PrintedLineItem
        }
      }
    }
  }
`);

export const PUBLIC_DOCUMENT_PAYMENT_SCHEDULE_QUERY = gql(`
  query PublicDocumentPaymentSchedule($id: ID!) {
    node(id: $id) {
      ... on PublicDocumentObject {
        id
        identifier
        paymentSchedule {
          id
          identifier
          name
          percent
          amount
          dateDue
          status
          datePaid
          dateClaimed
          claimedReference
        }
      }
    }
  }
`);

/** Journal Entry tab payload: lines are not on the base JournalEntry fragment. */
export const JOURNAL_ENTRY_LINES_QUERY = gql(`
  query JournalEntryLines($id: ID!, $pendingSession: String) {
    node(id: $id, pendingSession: $pendingSession) {
      ... on JournalEntryObject {
        id
        identifier
        updatedAt
        transactions {
          id
          identifier
          createdAt
          updatedAt
          debit
          credit
          description
          amountCurrency
          amountOriginal
          amountOriginalCurrency
          amountOriginalToAmountFx
          chartedAccount {
            id
            identifier
            name
            type
            path
          }
          tags {
            ...Tag
          }
          paymentFor {
            id
            identifier
            name
            reference
          }
        }
      }
    }
  }
`);

/**
 * The viewer's addresses they forward bills and receipts from: the one they
 * sign in with first, then those they added.
 */
export const EMAIL_ADDRESSES_QUERY = gql(`
  query EmailAddresses {
    emailAddresses {
      data {
        id
        identifier
        address
        isPrimary
      }
    }
  }
`);

/**
 * Settings › Emails: the viewer's own mailboxes, wherever each is given, and
 * those teammates shared in their businesses.
 */
export const EMAIL_CONNECTIONS_QUERY = gql(`
  query EmailConnections($entityIdentifiers: [String!]) {
    emailConnections(entityIdentifiers: $entityIdentifiers) {
      data {
        id
        identifier
        provider
        emailAddress
        status
        lastSearchedAt
        lastSearchFailure
        ownedByViewer
        ownerName
        assignments {
          id
          identifier
          visibility
          entity {
            id
            identifier
            name
            isUserEntity
          }
        }
      }
    }
  }
`);

/**
 * The Google accounts the person connected for their exports to be written to
 * as Google Sheets - theirs alone, wherever they work - newest first.
 */
export const GOOGLE_SHEETS_CONNECTIONS_QUERY = gql(`
  query GoogleSheetsConnections {
    emailConnections(provider: GOOGLE_SHEETS) {
      data {
        id
        identifier
        provider
        emailAddress
        status
      }
    }
  }
`);

/**
 * Triage: what an account has taken lately - its business's payments booked
 * there, those like this one first - to look over before posting it there too.
 */
export const TRIAGE_ACCOUNT_POSTINGS_QUERY = gql(`
  query TriageAccountPostings($id: ID!, $chartedAccountIdentifier: String!) {
    node(id: $id) {
      ... on TransactionObject {
        id
        accountPostings(chartedAccountIdentifier: $chartedAccountIdentifier) {
          isLike
          transaction {
            id
            identifier
            description
            amount
            effectiveDate
            categoryObj {
              id
              identifier
              name
              color
            }
          }
        }
      }
    }
  }
`);

/**
 * Triage: a duplicate's two payments, looked over side by side - each with the
 * statement it came in on, or else the first document it is on, its file to
 * preview.
 */
export const TRIAGE_DUPLICATE_STATEMENTS_QUERY = gql(`
  query TriageDuplicateStatements($id: ID!, $otherId: ID!) {
    payment: node(id: $id) {
      ... on TransactionObject {
        id
        identifier
        description
        amount
        effectiveDate
        financialAccount {
          id
          name
        }
        entity {
          id
          identifier
          name
        }
        lineItemOn {
          id
        }
        documents {
          id
          identifier
          name
          createdAt
          fromEmailAddress
          contentHash
          file {
            name
            url
          }
        }
      }
    }
    other: node(id: $otherId) {
      ... on TransactionObject {
        id
        identifier
        description
        amount
        effectiveDate
        financialAccount {
          id
          name
        }
        entity {
          id
          identifier
          name
        }
        lineItemOn {
          id
        }
        documents {
          id
          identifier
          name
          createdAt
          fromEmailAddress
          contentHash
          file {
            name
            url
          }
        }
      }
    }
  }
`);

/**
 * Triage: the drafts in review in a chat that a payment would take - a receipt
 * forwarded to its business and read by the AI, say - each with the chat it
 * waits in, and the entry it would book once accepted, to preview; one
 * confirmed there among them until the suggestion it makes is. Asked for by
 * the suggestions list, not the payment's own fragment.
 */
export const TRIAGE_DRAFT_MATCHES_QUERY = gql(`
  query TriageDraftMatches($id: ID!) {
    node(id: $id) {
      ... on TransactionObject {
        id
        draftMatches {
          confidence
          settlesElsewhere
          document {
            id
            identifier
            name
            amountTotal
            amountCurrency
            dateIssued
            fromEmailAddress
            externalProvider
            pendingChange {
              identifier
              session {
                identifier
              }
            }
            entity {
              id
              identifier
              name
            }
          }
          thread {
            id
            identifier
            name
          }
          inReview {
            id
            identifier
            job
            status
            lines {
              id
              identifier
              amount
              amountTax
              tax {
                id
                identifier
                name
              }
              description
              entity {
                id
                identifier
                name
              }
              chartedAccount {
                id
                identifier
                name
                path
                fullPath
              }
              catalogueAccountName
              catalogueAccountPath
              amountOriginal
              amountOriginalCurrency
              amountOriginalToAmountFx
            }
            ruleSuggestionDocumentOptions {
              id
              identifier
              amountTransaction
              amountTransactionCurrency
              amountDocument
              amountDocumentCurrency
              amountTransactionToDocumentFx
            }
          }
        }
        readingThread {
          id
          identifier
          name
        }
        readingFile {
          id
          identifier
          name
        }
      }
    }
  }
`);

/**
 * Triage: what the viewer's searches of their connected mailboxes found for a
 * payment - each attachment, or the email itself - likeliest first. Asked for
 * by the suggestions list alone, not the payment's own fragment.
 */
export const TRIAGE_EMAIL_CONNECTION_SEARCH_RESULTS_QUERY = gql(`
  query TriageEmailConnectionSearchResults($id: ID!) {
    node(id: $id) {
      ... on TransactionObject {
        id
        emailConnectionSearchResults {
          id
          identifier
          filename
          mimeType
          sender
          senderName
          subject
          sentAt
          amount
          currency
          exchangeRate
          exchangeRateSource
          amountFound
          nameFound
          matches
          isSent
          recipientName
          isPrivate
          isEmail
          query
          mailbox
          # Imported before: the document it was filed as, in its business,
          # and whether a payment - this one, or another - pays it.
          document {
            id
            identifier
            name
            documentType: type
            entity {
              id
              identifier
              name
            }
            paymentShares {
              transaction {
                id
                identifier
              }
            }
          }
        }
      }
    }
  }
`);

/**
 * A payment's currency, and what it was charged in another as its bank's
 * statement prints it with the official rate the day it posted - for the
 * documents found for it, set beside them.
 */
export const TRIAGE_FOREIGN_CHARGE_QUERY = gql(`
  query TriageForeignCharge($id: ID!) {
    node(id: $id) {
      ... on TransactionObject {
        id
        amountCurrency
        foreignCharge {
          amountOriginal
          amountOriginalCurrency
          amountOriginalToAmountFx
          officialAmountOriginalToAmountFx
          officialFxSource
        }
      }
    }
  }
`);

/** A document found in a mailbox, fetched from it to preview before it is imported. */
export const EMAIL_CONNECTION_SEARCH_RESULT_PREVIEW_QUERY = gql(`
  query EmailConnectionSearchResultPreview($id: ID!) {
    node(id: $id) {
      ... on EmailConnectionSearchResultObject {
        id
        preview {
          filename
          mimeType
          content
        }
      }
    }
  }
`);

/** Settings integrations panel payload: bank feeds are not on the base Entity fragment. */
export const ENTITY_INTEGRATIONS_QUERY = gql(`
  query EntityIntegrations($entityIdentifiers: [String!]) {
    financialInstitutions(entityIdentifiers: $entityIdentifiers) {
      data {
        id
        identifier
        institutionName
        organization {
          id
          identifier
          name
          logoUrl
        }
        financialAccounts {
          ... on FinancialAccountObject {
            id
            identifier
            name
            mask
            syncState
            syncCompletedAt
          }
        }
      }
    }
  }
`);

export const ENTITY_MEMBERS_QUERY = gql(`
  query EntityMembers($id: ID!) {
    node(id: $id) {
      ... on EntityObject {
        id
        identifier
        accounts {
          id
          identifier
          isOwner
          isExternal
          externalGrantIdentifier
          firmName
          permissions
          status
          invitedAt
          memberEmailAddress
          memberDisplayName
          role {
            id
            identifier
            name
          }
        }
      }
    }
  }
`);

/**
 * A person's notifications, latest first, for the popup: what each says, and
 * the export it is about - whose rows it holds, and its file once written - or
 * the chat a forwarded email started, which it opens. Its own shape rather
 * than ``...Notification``, which carries every record a notification can
 * name.
 */
export const INBOX_CONNECTION_QUERY = gql(`
  query Inbox(
    $entityIdentifiers: [String!]
    $filter: NotificationFilter
    $first: Int
    $after: String
  ) {
    notificationsConnection(entityIdentifiers: $entityIdentifiers) {
      data(filter: $filter, first: $first, after: $after) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            identifier
            createdAt
            message
            isRead
            bulkOperation {
              id
              identifier
              operation
              name
              status
              rowCount
              entities {
                id
                identifier
                name
              }
              file {
                name
                url
              }
              spreadsheetUrl
            }
            thread {
              id
              identifier
              name
            }
          }
        }
      }
    }
  }
`);

export const THREADS_CONNECTION_QUERY = gql(`
  query Threads(
    $entityIdentifiers: [String!]
    $filter: ThreadFilter
    $first: Int
    $after: String
  ) {
    threadsConnection(entityIdentifiers: $entityIdentifiers) {
      data(
        first: $first
        after: $after
        filter: $filter
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...ThreadListItem
          }
        }
      }
    }
  }
`);

export const THREAD_WITH_MESSAGES_QUERY = gql(`
  query ThreadWithMessages($id: ID!, $last: Int, $before: String) {
    node(id: $id) {
      ... on ThreadObject {
        ...Thread
        messages(last: $last, before: $before) {
          pageInfo {
            hasPreviousPage
            startCursor
          }
          edges {
            node {
              ...ThreadMessage
            }
          }
        }
        pendingSession {
          identifier
          changes {
            identifier
            changeType
            objectIdentifier
            modelName
            status
            parent {
              identifier
            }
            objectEntity {
              id
              identifier
            }
            node {
              id
              identifier
              __typename
              ... on ContactObject {
                name
                emailAddress
              }
              ... on DocumentObject {
                name
                reference
                description
              }
              ... on TransactionObject {
                description
              }
              ... on JournalEntryObject {
                description
              }
              ... on TagObject {
                name
              }
              ... on ChartedAccountObject {
                name
              }
              ... on FinancialInstitutionObject {
                institutionName
              }
              ... on FinancialAccountObject {
                name
              }
              ... on OrganizationObject {
                name
              }
              ... on RuleDefinitionObject {
                name
              }
            }
          }
        }
      }
    }
  }
`);

/**
 * The thread's open review, alone. Read while a turn runs, each time a tool
 * call reports a change the tray has not seen — one call can make several —
 * so the tray fills as the agent works rather than when it stops.
 */
export const THREAD_PENDING_SESSION_QUERY = gql(`
  query ThreadPendingSession($id: ID!) {
    node(id: $id) {
      ... on ThreadObject {
        id
        pendingSession {
          identifier
          status
          changeCount
          pendingChangeCount
          hasPendingChanges
          changes {
            identifier
            changeType
            objectIdentifier
            modelName
            status
            parent {
              identifier
            }
            objectEntity {
              id
              identifier
            }
            node {
              id
              identifier
              __typename
              ... on ContactObject {
                name
                emailAddress
              }
              ... on DocumentObject {
                name
                reference
                description
              }
              ... on TransactionObject {
                description
              }
              ... on JournalEntryObject {
                description
              }
              ... on TagObject {
                name
              }
              ... on ChartedAccountObject {
                name
              }
              ... on FinancialInstitutionObject {
                institutionName
              }
              ... on FinancialAccountObject {
                name
              }
              ... on OrganizationObject {
                name
              }
              ... on RuleDefinitionObject {
                name
              }
            }
          }
        }
      }
    }
  }
`);

export const THREAD_CHANGES_QUERY = gql(`
  query ThreadChanges($id: ID!) {
    node(id: $id) {
      ... on ThreadObject {
        id
        changes {
          identifier
          changeType
          objectIdentifier
          modelName
          status
          parent {
            identifier
          }
          objectEntity {
            id
            identifier
          }
          node {
            id
            identifier
            __typename
            ... on ContactObject {
              name
              emailAddress
            }
            ... on DocumentObject {
              name
              reference
              description
            }
            ... on TransactionObject {
              description
            }
            ... on JournalEntryObject {
              description
            }
            ... on TagObject {
              name
            }
            ... on ChartedAccountObject {
              name
            }
            ... on FinancialInstitutionObject {
              institutionName
            }
            ... on FinancialAccountObject {
              name
            }
            ... on OrganizationObject {
              name
            }
            ... on RuleDefinitionObject {
              name
            }
          }
        }
      }
    }
  }
`);

export const THREAD_LOAD_MORE_MESSAGES_QUERY = gql(`
  query ThreadLoadMoreMessages($id: ID!, $last: Int!, $before: String!) {
    node(id: $id) {
      ... on ThreadObject {
        id
        messages(last: $last, before: $before) {
          pageInfo {
            hasPreviousPage
            startCursor
          }
          edges {
            node {
              ...ThreadMessage
            }
          }
        }
      }
    }
  }
`);

export const JOURNAL_ENTRIES_CONNECTION_QUERY = gql(`
  query JournalEntries(
    $entityIdentifiers: [String!]
    $filter: JournalEntryFilter
    $first: Int
    $after: String
  ) {
    journalEntriesConnection(entityIdentifiers: $entityIdentifiers) {
      data(first: $first, after: $after, filter: $filter) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...JournalEntry
          }
        }
      }
    }
  }
`);

export const TAXES_CONNECTION_QUERY = gql(`
  query TaxesConnection(
    $entityIdentifiers: [String!]
    $filter: TaxFilter
    $first: Int
    $after: String
  ) {
    taxesConnection(entityIdentifiers: $entityIdentifiers) {
      data(first: $first, after: $after, filter: $filter) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...Tax
          }
        }
      }
    }
  }
`);

export const REPORTS_CONNECTION_QUERY = gql(`
  query ReportsConnection(
    $entityIdentifiers: [String!]
    $filter: ReportFilter
    $first: Int
    $after: String
  ) {
    reportsConnection(entityIdentifiers: $entityIdentifiers) {
      data(first: $first, after: $after, filter: $filter) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            ...ViewLayout
          }
        }
      }
    }
  }
`);

export const PEOPLES_CONNECTION_QUERY = gql(`
  query Peoples(
    $entityIdentifiers: [String!]
    $filter: ContactFilter
    $first: Int
    $after: String
  ) {
    contactsConnection(entityIdentifiers: $entityIdentifiers) {
      data(first: $first, after: $after, filter: $filter) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            ...Contact
          }
        }
      }
    }
  }
`);

export const ORGANIZATIONS_CONNECTION_QUERY = gql(`
  query Organizations(
    $entityIdentifiers: [String!]
    $filter: OrganizationFilter
    $first: Int
    $after: String
  ) {
    organizationsConnection(entityIdentifiers: $entityIdentifiers) {
      data(first: $first, after: $after, filter: $filter) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            ...Organization
          }
        }
      }
    }
  }
`);

/** Brands Brandfetch knows by a name like ``query``, for a picker as the person types: live, never kept. */
export const BRANDS_QUERY = gql(`
  query Brands($query: String!) {
    brands(query: $query) {
      data {
        name
        domain
        logoUrl
      }
    }
  }
`);

export const SEARCH_QUERY = gql(`
  query Search($queryStr: String!, $startDate: DateTime, $endDate: DateTime, $entityIdentifiers: [String!]) {
    search(queryStr: $queryStr, entityIdentifiers: $entityIdentifiers) {
      data {
        ...SearchResult
      }
      errors {
        ...Errors
      }
    }
  }
`);

export const TRANSACTIONS_CONNECTION_QUERY = gql(`
  query Transactions(
    $entityIdentifiers: [String!]!
    $filter: TransactionFilter
    $order: [TransactionOrder!]
    $first: Int
    $after: String
  ) {
    transactionsConnection(entityIdentifiers: $entityIdentifiers) {
      data(
        first: $first
        after: $after
        filter: $filter
        order: $order
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...Transaction
          }
        }
      }
    }
  }
`);

export const RULES_CONNECTION_QUERY = gql(`
  query Rules(
    $entityIdentifiers: [String!]
    $filter: RuleDefinitionFilter
    $order: [RuleDefinitionOrder!]
    $first: Int
    $after: String
  ) {
    rulesConnection(entityIdentifiers: $entityIdentifiers) {
      data(
        first: $first
        after: $after
        filter: $filter
        order: $order
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...Rule
            ruleStatus: status
          }
        }
      }
    }
  }
`);

/**
 * Rule graph fetch for the editor.
 *
 * ``Rule`` is also selected on ``NODE_QUERY`` / ``NODE_QUERY_WITH_PENDING_CHANGE``
 * so the node panel hydrates without a second round-trip. This dedicated query still exists for the
 * full-page editor, which is not behind the node panel loader.
 */
export const RULE_QUERY = gql(`
  query Rule($id: ID!) {
    node(id: $id) {
      ... on RuleDefinitionObject {
        ...Rule
        ruleStatus: status
      }
    }
  }
`);

export const RULE_EXECUTION_LOGS_CONNECTION_QUERY = gql(`
  query ExecutionsConnection(
    $entityIdentifiers: [String!]
    $ruleIdentifier: String
    $order: [ExecutionOrder!]
    $first: Int
    $after: String
  ) {
    executionsConnection(
      entityIdentifiers: $entityIdentifiers
      ruleIdentifier: $ruleIdentifier
    ) {
      errors {
        __typename
      }
      data(
        first: $first
        after: $after
        order: $order
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...ExecutionListItem
          }
        }
      }
    }
  }
`);

export const TRIAGE_CONNECTION_QUERY = gql(`
  query TriageConnection(
    $entityIdentifiers: [String!]!
    $filter: TransactionFilter
    $order: [TransactionOrder!]
    $first: Int
    $after: String
    $includesBooked: Boolean
  ) {
    triageConnection(
      entityIdentifiers: $entityIdentifiers
      includesBooked: $includesBooked
    ) {
      data(
        first: $first
        after: $after
        filter: $filter
        order: $order
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            ...TriageListItem
          }
        }
      }
    }
  }
`);

/**
 * The payments like the one looked at in triage, before it is booked or
 * moved: the others of its payee in every business the person is in, booked
 * or not - drawn as the queue draws its own, each beside what it is booked to
 * now and what it would be booked to like it, as its Book is about to book it
 * (the lines it sends, what they link and how another business settles it),
 * or as it would move.
 */
export const TRIAGE_BOOK_LIKE_QUERY = gql(`
  query TriageBookLike(
    $id: ID!
    $payment: String!
    $lines: [BookLineInput!]
    $links: BookLinksInput
    $treatment: SettlementTreatmentEnum
    $parts: [BookingPartEnum!]
    $documents: Boolean = false
  ) {
    node(id: $id) {
      id
      ... on TransactionObject {
        payeePayments(
          lines: $lines
          links: $links
          treatment: $treatment
          withItself: true
          parts: $parts
        ) {
          bookedAs
          bookedAsLike
          # The same in parts, for the filters' before and after.
          booked {
            businessIdentifier
            businessName
            treatment
            isBooked
            accounts
            category
          }
          bookedLike {
            businessIdentifier
            businessName
            treatment
            isBooked
            accounts
            category
          }
          payment {
            id
            identifier
            # Each to its own document: what a search of the mailboxes found
            # for it, where it has none in the books - the one to import.
            emailConnectionSearchResults @include(if: $documents) {
              id
              identifier
              subject
              filename
              amount
              currency
              sentAt
              amountFound
              isPrivate
            }
          }
          # Each to its own document: the one it would pay - a record, or a
          # chat's draft.
          document @include(if: $documents) {
            id
            identifier
            name
            pendingChange {
              identifier
            }
          }
          isDocumentMissing @include(if: $documents)
        }
      }
    }
    triageConnection(entityIdentifiers: [], includesBooked: true) {
      data(first: 100, filter: { payeeOf: $payment }) {
        totalCount
        edges {
          node {
            ...TriageListItem
          }
        }
      }
    }
  }
`);

/**
 * The other side each of a payment's payee's others is offered by its
 * suggestions - a transfer's, or the payment it copies - asked for as Similar
 * lines them up with those, and not before.
 */
export const TRIAGE_BOOK_LIKE_COUNTERPARTS_QUERY = gql(`
  query TriageBookLikeCounterparts($payment: String!) {
    triageConnection(entityIdentifiers: [], includesBooked: true) {
      data(first: 100, filter: { payeeOf: $payment }) {
        edges {
          node {
            id
            identifier
            suggestions {
              id
              identifier
              job
              status
              ruleSuggestionTransactionOptions {
                id
                identifier
                transaction {
                  id
                  identifier
                  description
                  amount
                  effectiveDate
                  entity {
                    id
                    identifier
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

/**
 * Which of a payment's payee's others the queue's filter keeps - its search
 * and its queues - so Similar is narrowed by the bar it shares with the queue.
 */
export const TRIAGE_PAYEE_OF_NARROWED_QUERY = gql(`
  query TriagePayeeOfNarrowed($payment: String!, $narrowedBy: TransactionFilter!) {
    triageConnection(entityIdentifiers: [], includesBooked: true) {
      data(first: 100, filter: { all: [{ payeeOf: $payment }, $narrowedBy] }) {
        edges {
          node {
            id
            identifier
          }
        }
      }
    }
  }
`);

/** The queue's cards of the payments named: what Similar lines a payment up with, drawn as the payment is. */
export const TRIAGE_COUNTERPART_CARDS_QUERY = gql(`
  query TriageCounterpartCards($identifiers: [ID!]) {
    triageConnection(entityIdentifiers: [], includesBooked: true) {
      data(
        first: 100
        filter: { field: { identifier: { inList: $identifiers } } }
      ) {
        edges {
          node {
            ...TriageListItem
          }
        }
      }
    }
  }
`);

/**
 * The documents a payment's payee's others might pay, newest first, to be
 * lined up with them - each with the payment its rules most plainly pair it
 * with.
 */
export const TRIAGE_PAYEE_DOCUMENTS_QUERY = gql(`
  query TriagePayeeDocuments($id: ID!, $transactions: [String!]!) {
    node(id: $id) {
      id
      ... on TransactionObject {
        payeeDocuments(transactionIdentifiers: $transactions) {
          pairedWith
          offeredTo
          document {
            id
            identifier
            name
            reference
            amountTotal
            amountCurrency
            dateIssued
            pendingChange {
              identifier
            }
            entity {
              id
              identifier
              name
            }
            # Who it is from: what goes with it where it is moved.
            contact {
              id
              identifier
              emailAddress
              organization {
                id
                identifier
                name
              }
            }
          }
        }
      }
    }
  }
`);

/** How many open payments each triage queue holds, for the queue's filter. */
export const TRIAGE_QUEUE_COUNTS_QUERY = gql(`
  query TriageQueueCounts($entityIdentifiers: [String!]) {
    triageQueueCounts(entityIdentifiers: $entityIdentifiers) {
      errors {
        ...Errors
      }
      data {
        queue
        count
      }
    }
  }
`);

/**
 * Detail-panel payload: suggestions are not on the base Triage fragment. Lean
 * stubs only — full RuleSuggestion option trees are loaded by catalog tabs /
 * generate-new, not on every row selection.
 */
export const TRIAGE_DETAIL_QUERY = gql(`
  query TriageDetail($id: ID!) {
    node(id: $id) {
      ... on TransactionObject {
        ...Triage
        suggestions {
          id
          identifier
          updatedAt
          status
          confidence
          rationale
          selectionRationale
          job
          source
          metadata
          # How it settles in another business unless said otherwise: on its
          # card, and on the row's half picked, from the first draw.
          recommendedTreatment
          lines {
            id
            identifier
            amount
            amountTax
            tax {
              id
              identifier
              name
            }
            description
            entity {
              id
              identifier
              name
            }
            chartedAccount {
              id
              identifier
              name
              path
              fullPath
            }
            catalogueAccountName
            catalogueAccountPath
            amountOriginal
            amountOriginalCurrency
            amountOriginalToAmountFx
          }
          rule {
            id
            identifier
            name
            metadata
            triggerType
            triggerObjectType
            triggerOperation
            scheduleFrequency
            conditions(includePending: true) {
              connector
              isNegated
              field
              operator
              valueType
              value
              children {
                connector
                isNegated
                field
                operator
                valueType
                value
                children {
                  connector
                  isNegated
                  field
                  operator
                  valueType
                  value
                }
              }
            }
          }
          matchDocumentDefinition {
            ...RuleDefinitionSuggestionMatchDocument
          }
          newCategoryName
          ruleSuggestionDocumentOptions {
            id
            identifier
            # A unit of its document's currency in the payment's, where they
            # differ.
            exchangeRate
            exchangeRateSource
            # What the payment would pay of it, on both sides: the entry's
            # "converted from" note.
            amountTransaction
            amountTransactionCurrency
            amountDocument
            amountDocumentCurrency
            amountTransactionToDocumentFx
            document {
              id
              identifier
              name
              reference
              fromEmailAddress
              externalProvider
              description
              amountTotal
              amountDue
              amountTax
              amountCurrency
              dateIssued
              dateDue
              createdAt
              documentType: type
              # The payments it is paid by already: one other than this
              # payment is a match booking it keeps, or replaces.
              paymentShares {
                amountTransaction
                amountTransactionCurrency
                amountDocument
                amountDocumentCurrency
                amountTransactionToDocumentFx
                transaction {
                  id
                  identifier
                  description
                  effectiveDate
                  amount
                  paymentFor {
                    id
                    identifier
                    name
                  }
                  entity {
                    id
                    identifier
                    name
                  }
                  financialAccount {
                    id
                    identifier
                    name
                  }
                }
              }
              contact {
                id
                identifier
                emailAddress
                organization {
                  id
                  identifier
                  name
                  logoUrl
                }
              }
              entity {
                id
                identifier
                name
              }
              file {
                name
              }
            }
          }
          ruleSuggestionChartedAccountOption {
            id
            identifier
            chartedAccount {
              id
              identifier
              name
              path
              fullPath
            }
            category {
              id
              identifier
              name
              color
            }
          }
          ruleSuggestionTransactionOptions {
            id
            identifier
            transaction {
              id
              identifier
              description
              amount
              effectiveDate
              externalCreatedAt
              financialAccount {
                id
                identifier
                name
              }
              entity {
                id
                identifier
                name
              }
            }
          }
          ruleSuggestionTagOptions {
            id
            identifier
            tag {
              id
              identifier
              name
              color
            }
          }
        }
      }
    }
  }
`);

const REPORTS_QUERY = gql(`
  query Reports {
    reports {
      data {
        id
        identifier
        name
        entities {
          id
          identifier
          name
        }
        definition {
          ...ReportDefinition
        }
        entity {
          id
          identifier
          name
        }
        linkSharingEnabled
        shareToken
      }
    }
  }
`);

/** What a report widget can be of, and how each source may be drawn. */
export const REPORT_SOURCE_CATALOG_QUERY = gql(`
  query ReportSourceCatalog {
    reportSourceCatalog {
      data {
        name
        label
        presentations
        filters
        isSummarized
        fields {
          name
          label
          kind
          typename
          isGroupable
          isMeasurable
          takesInterval
        }
      }
      errors {
        ...Errors
      }
    }
  }
`);

export const REPORT_FILTER_DEFINITIONS_QUERY = gql(`
  query ReportFilterDefinitions($construct: String) {
    reportFilterDefinitions(construct: $construct) {
      key
      type
      name
      operators {
        key
        name
        symbol
      }
      valueOptions {
        key
        value
      }
    }
  }
`);

export const SHARED_REPORT_SHARE_CHALLENGE_MUTATION = gql(`
  mutation SharedReportShareChallenge($token: String!) {
    sharedReportShareChallenge(token: $token) {
      layoutName
      reportIdentifier
      requiresSignIn
    }
  }
`);

export const AUTHORIZE_SHARED_REPORT_ACCESS_MUTATION = gql(`
  mutation AuthorizeSharedReportAccess($token: String!) {
    authorizeSharedReportAccess(token: $token) {
      ok
      sessionToken
    }
  }
`);

export const SHARED_VIEW_SHELL_QUERY = gql(`
  query SharedViewShell($id: ID!) {
    node(id: $id) {
      ... on SharedReportObject {
        name
        definition {
          ...ReportDefinition
        }
        entityNames
        defaultEntityIdentifiers
        groups {
          id
          name
        }
      }
    }
  }
`);

/**
 * How a suggestion that books in two businesses can be settled between them - each way with the entries it
 * would book in both. Only the preview and the accept prompt need it, and only for such a suggestion.
 */
export const TRIAGE_SUGGESTION_SETTLEMENT_QUERY = gql(`
  query TriageSuggestionSettlement($id: ID!) {
    node(id: $id) {
      ... on RuleSuggestionObject {
        id
        identifier
        settlementTreatments {
          treatment
          title
          description
          isLoan
          isRecommended
          lines {
            entity {
              id
              identifier
              name
            }
            chartedAccount {
              id
              identifier
              name
            }
            accountName
            amount
            description
            role
          }
        }
      }
    }
  }
`);

/** The chart's accounts a budget's plan can put a line on, and what each account is: for its picker. */
export const BUDGET_PLAN_ACCOUNTS_QUERY = gql(`
  query BudgetPlanAccounts($entityIdentifiers: [String!]) {
    chartedAccounts(entityIdentifiers: $entityIdentifiers) {
      data {
        id
        identifier
        name
        fullPath
        type
      }
    }
  }
`);

export const TRIAGE_CHARTED_ACCOUNT_CATALOG_QUERY = gql(`
  query TriageChartedAccountCatalog($entityIdentifiers: [String!]) {
    chartedAccounts(entityIdentifiers: $entityIdentifiers) {
      data {
        id
        identifier
        path
        fullPath
        name
        description
        categories {
          id
          identifier
          name
          color
        }
      }
    }
  }
`);

/**
 * The ways a business could book a payment the words describe, best first:
 * its own accounts and categories, and the catalogue's it has not made yet -
 * by meaning, where it can be had, else by how alike the words read.
 */
export const WAYS_TO_BOOK_QUERY = gql(`
  query WaysToBook($entityIdentifier: String!, $words: String!) {
    waysToBook(entityIdentifier: $entityIdentifier, words: $words) {
      data {
        key
        catalogueKey
        accountName
        categoryName
        score
        chartedAccount {
          id
          identifier
          path
          fullPath
          name
          description
          categories {
            id
            identifier
            name
            color
          }
        }
        category {
          id
          identifier
          name
          color
        }
      }
    }
  }
`);

/** A business's budgets, each with the month asked for worked out from its books: what is left to spend and how the
 * month is going, what was needed, wanted and put by, what needs attention - each naming its lines by id - and every
 * line's planned, spent and left - a goal's saved, a debt's still owed - and its unpaid bills. */
export const BUDGETS_QUERY = gql(`
  query Budgets($entityIdentifiers: [String!], $month: Date) {
    budgets(entityIdentifiers: $entityIdentifiers) {
      errors {
        ...Errors
      }
      data {
        ...Budget
        month(month: $month) {
          ...BudgetMonth
        }
      }
    }
  }
`);

/** A business's budgets by identifier alone, nothing of them worked out: whether it has one, for the phone's tab bar
 * to give Budget a place. */
export const BUDGET_IDS_QUERY = gql(`
  query BudgetIds($entityIdentifiers: [String!]) {
    budgets(entityIdentifiers: $entityIdentifiers) {
      errors {
        ...Errors
      }
      data {
        id
        identifier
      }
    }
  }
`);

/** What starting a budget would offer: what a middling month brings in, a line for each account spent on lately or
 * that a household plans - a bill, everyday or paying off, needed or wanted, spread or not, at a middling month - and
 * the debts it could pay off. */
export const BUDGET_SETUP_QUERY = gql(`
  query BudgetSetup($entityIdentifier: String!) {
    budgetSetup(entityIdentifier: $entityIdentifier) {
      errors {
        ...Errors
      }
      data {
        amountIncome
        incomeAccounts {
          id
          identifier
          name
        }
        uncountedCurrencies
        hasBankAccounts
        paymentCount
        waitingCount
        lines {
          kind
          bucket
          isSpread
          amountSuggested
          chartedAccount {
            id
            identifier
            name
            path
            parent {
              id
              name
            }
          }
        }
        debts {
          amountOwed
          chartedAccount {
            id
            identifier
            name
          }
        }
      }
    }
  }
`);

/** A line's month and the payments on it, newest first: for its panel. */
export const BUDGET_LINE_MONTH_QUERY = gql(`
  query BudgetLineMonth($id: ID!, $month: Date) {
    node(id: $id) {
      ... on BudgetLineObject {
        id
        month(month: $month) {
          amountPlanned
          amountSpent
          amountDue
          amountLeft
          amountSaved
          amountOwed
          amountNeeded
          bills {
            id
            identifier
            name
            amountTotal
            amountDue
            dateDue
          }
        }
        history(month: $month) {
          on
          description
          amount
          amountCurrency
          transaction {
            id
            identifier
          }
        }
      }
    }
  }
`);

/** What booking a payment's lines would leave on each line of the budget they land on, this month: said beside
 * Book. */
export const BUDGET_LINES_AFTER_QUERY = gql(`
  query BudgetLinesAfter(
    $entityIdentifier: String!
    $lines: [BudgetLineAfterInputObject!]!
    $amountCurrency: String!
  ) {
    budgetLinesAfter(
      entityIdentifier: $entityIdentifier
      lines: $lines
      amountCurrency: $amountCurrency
    ) {
      errors {
        ...Errors
      }
      data {
        amountLeft
        amountAfter
        line {
          id
          identifier
          name
          kind
          amountCurrency
          entity {
            id
            currency
          }
          chartedAccount {
            id
            name
          }
        }
      }
    }
  }
`);

/** The lines of the budget a payment's booking, or a bill's lines, land on, with what each has left this month: for
 * its panel. */
export const RECORD_BUDGET_LINES_QUERY = gql(`
  query RecordBudgetLines($id: ID!) {
    node(id: $id) {
      ... on TransactionObject {
        id
        budgetLines {
          amountLeft
          line {
            id
            identifier
            name
            kind
            amountCurrency
            entity {
              id
              currency
            }
            chartedAccount {
              id
              name
            }
          }
        }
      }
      ... on DocumentObject {
        id
        budgetLines {
          amountLeft
          line {
            id
            identifier
            name
            kind
            amountCurrency
            entity {
              id
              currency
            }
            chartedAccount {
              id
              name
            }
          }
        }
      }
    }
  }
`);

const Queries = {
  RECORD_BUDGET_LINES_QUERY,
  BUDGET_LINES_AFTER_QUERY,
  BUDGET_PLAN_ACCOUNTS_QUERY,
  BUDGETS_QUERY,
  BUDGET_IDS_QUERY,
  BUDGET_SETUP_QUERY,
  BUDGET_LINE_MONTH_QUERY,
  ACCOUNTS_QUERY,
  WAYS_TO_BOOK_QUERY,
  FINANCIAL_ACCOUNTS_CONNECTION_QUERY,
  BILLING_PLANS_QUERY,
  CASHFLOW_STATEMENT_QUERY,
  SUMMARY_QUERY,
  DOCUMENTS_CONNECTION_QUERY,
  ENTITY_COMPANY_DOCUMENTS_QUERY,
  DOCUMENT_TEMPLATES_QUERY,
  DOCUMENT_PREVIEW_QUERY,
  DOCUMENT_IS_BOOKED_QUERY,
  DOCUMENT_LINE_ITEMS_QUERY,
  DOCUMENT_PAYMENTS_QUERY,
  FINANCIAL_ACCOUNT_STATEMENTS_QUERY,
  DOCUMENT_PAYMENT_SCHEDULE_QUERY,
  PUBLIC_DOCUMENT_LINE_ITEMS_QUERY,
  PUBLIC_DOCUMENT_PAYMENT_SCHEDULE_QUERY,
  JOURNAL_ENTRY_LINES_QUERY,
  ENTITY_MEMBERS_QUERY,
  ENTITY_INTEGRATIONS_QUERY,
  EMAIL_ADDRESSES_QUERY,
  EMAIL_CONNECTIONS_QUERY,
  GOOGLE_SHEETS_CONNECTIONS_QUERY,
  TRIAGE_DRAFT_MATCHES_QUERY,
  TRIAGE_ACCOUNT_POSTINGS_QUERY,
  TRIAGE_DUPLICATE_STATEMENTS_QUERY,
  TRIAGE_EMAIL_CONNECTION_SEARCH_RESULTS_QUERY,
  TRIAGE_FOREIGN_CHARGE_QUERY,
  EMAIL_CONNECTION_SEARCH_RESULT_PREVIEW_QUERY,
  THREADS_CONNECTION_QUERY,
  THREAD_WITH_MESSAGES_QUERY,
  THREAD_CHANGES_QUERY,
  THREAD_PENDING_SESSION_QUERY,
  THREAD_LOAD_MORE_MESSAGES_QUERY,
  INBOX_CONNECTION_QUERY,
  JOURNAL_ENTRIES_CONNECTION_QUERY,
  TAXES_CONNECTION_QUERY,
  MESSAGE_FILE_QUERY,
  NODE_QUERY,
  RULE_SUGGESTION_COMPETING_QUERY,
  RULE_SUGGESTION_OTHER_PARTS_QUERY,
  NODE_QUERY_WITH_PENDING_CHANGE,
  NODE_EMBEDDING_STATE,
  PENDING_CHANGE_QUERY,
  PEOPLES_CONNECTION_QUERY,
  ORGANIZATIONS_CONNECTION_QUERY,
  BRANDS_QUERY,
  REPORTS_CONNECTION_QUERY,
  PUBLIC_NODE_QUERY,
  PUBLIC_EXTERNAL_GRANT_QUERY,
  PUBLIC_ACCOUNT_QUERY,
  SEARCH_QUERY,
  TRANSACTIONS_CONNECTION_QUERY,
  USER_QUERY,
  RULE_QUERY,
  RULES_CONNECTION_QUERY,
  RULE_EXECUTION_LOGS_CONNECTION_QUERY,
  PENDING_INVITATIONS_QUERY,
  EXTERNAL_GRANTS_QUERY,
  VERIFIED_FIRMS_QUERY,
  EXTERNAL_CLIENTS_QUERY,
  EXTERNAL_GRANTS_RECEIVED_QUERY,
  ENTITY_ROLES_QUERY,
  REPORTS_QUERY,
  REPORT_FILTER_DEFINITIONS_QUERY,
  REPORT_SOURCE_CATALOG_QUERY,
  TRIAGE_CONNECTION_QUERY,
  TRIAGE_DETAIL_QUERY,
  TRIAGE_BOOK_LIKE_QUERY,
  TRIAGE_BOOK_LIKE_COUNTERPARTS_QUERY,
  TRIAGE_COUNTERPART_CARDS_QUERY,
  TRIAGE_PAYEE_OF_NARROWED_QUERY,
  TRIAGE_PAYEE_DOCUMENTS_QUERY,
  TRIAGE_QUEUE_COUNTS_QUERY,
  TRIAGE_CHARTED_ACCOUNT_CATALOG_QUERY,
  TRIAGE_SUGGESTION_SETTLEMENT_QUERY,
};

export { Queries };
